import { useMeeting, useParticipant, VideoPlayer } from "@videosdk.live/react-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactPlayer from "react-player";
import MicOffSmallIcon from "../icons/MicOffSmallIcon";
import ScreenShareIcon from "../icons/ScreenShareIcon";
import SpeakerIcon from "../icons/SpeakerIcon";
import { nameTructed } from "../utils/helper";
import { CornerDisplayName } from "./ParticipantView";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import useIsMobile from "../hooks/useIsMobile";
import { useBreakoutRooms } from "../context/BreakoutRoomsContext";
import logger from '../utils/logger';

export function PresenterView({ height }) {
  const mMeeting = useMeeting();
  const presenterId = mMeeting?.presenterId;
  const { isInSameRoom, currentRoom, participantRooms } = useBreakoutRooms();
  const {
    micOn,
    webcamOn,
    isLocal,
    screenShareAudioStream,
    screenShareOn,
    displayName,
    isActiveSpeaker,
  } = useParticipant(presenterId);
  const {
    sideBarMode,
    raisedHandsParticipants,
    pipMode,
    pipClosedByUser,
    restoreScreenSharePipRef,
    setPipClosedByUser,
  } = useMeetingAppContext();

  const audioPlayer = useRef();
  const containerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isMobile = useIsMobile();

  // Pinch-to-zoom state for mobile
  const [zoomState, setZoomState] = useState({
    scale: 1,
    translateX: 0,
    translateY: 0,
  });
  const [isZooming, setIsZooming] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const touchStateRef = useRef({
    initialDistance: 0,
    initialScale: 1,
    initialTranslateX: 0,
    initialTranslateY: 0,
    lastTouchTime: 0,
    lastPanX: 0,
    lastPanY: 0,
    lastTapX: 0,
    lastTapY: 0,
    tapTimeout: null,
    hasMoved: false,
    startX: 0,
    startY: 0,
  });

  // Filter screen share by breakout room - only show screen share from participants in the same room
  const shouldShowScreenShare = useMemo(() => {
    if (!presenterId) return false;
    if (isLocal) return true; // Always show own screen share

    if (!currentRoom) {
      // If in main meeting, only show screen shares from participants NOT in any breakout room
      return !participantRooms[presenterId];
    }

    // If in a breakout room, only show screen share if presenter is in the same room
    return isInSameRoom(presenterId);
  }, [presenterId, isLocal, currentRoom, isInSameRoom, participantRooms]);

  // Check if the presenter has raised their hand
  const hasRaisedHand =
    presenterId &&
    raisedHandsParticipants.some(
      (raisedHand) => raisedHand.participantId === presenterId
    );

  useEffect(() => {
    if (
      !isLocal &&
      audioPlayer.current &&
      screenShareOn &&
      screenShareAudioStream
    ) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(screenShareAudioStream.track);

      audioPlayer.current.srcObject = mediaStream;
      audioPlayer.current.play().catch((err) => {
        if (
          err.message ===
          "play() failed because the user didn't interact with the document first. https://goo.gl/xX8pDD"
        ) {
          logger("error", "audio" + err.message);
        }
      });
    } else {
      audioPlayer.current.srcObject = null;
    }
  }, [screenShareAudioStream, screenShareOn, isLocal]);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
      if (!document.fullscreenElement) {
        setZoomState({ scale: 1, translateX: 0, translateY: 0 });
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    document.addEventListener("msfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      document.removeEventListener("msfullscreenchange", onFsChange);
    };
  }, []);

  useEffect(() => {
    if (!screenShareOn) {
      setZoomState({ scale: 1, translateX: 0, translateY: 0 });
    }
  }, [screenShareOn]);

  // Don't render if screen share should be filtered out
  if (!shouldShowScreenShare) {
    return null;
  }

  const getDistance = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getCenter = (touch1, touch2) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  };

  const constrainTranslate = (
    scale,
    translateX,
    translateY,
    containerWidth,
    containerHeight
  ) => {
    if (!videoContainerRef.current) return { translateX, translateY };

    const videoElement = videoContainerRef.current.querySelector("video");
    if (!videoElement) return { translateX, translateY };

    const videoWidth = videoElement.videoWidth || containerWidth;
    const videoHeight = videoElement.videoHeight || containerHeight;
    const scaledWidth = videoWidth * scale;
    const scaledHeight = videoHeight * scale;

    const maxTranslateX = Math.max(0, (scaledWidth - containerWidth) / 2);
    const maxTranslateY = Math.max(0, (scaledHeight - containerHeight) / 2);

    return {
      translateX: Math.max(-maxTranslateX, Math.min(maxTranslateX, translateX)),
      translateY: Math.max(-maxTranslateY, Math.min(maxTranslateY, translateY)),
    };
  };

  // Handle touch start
  const handleTouchStart = (e) => {
    if (!isMobile || isLocal || !screenShareOn) return;

    const touches = e.touches;
    if (touches.length === 1) {
      const touch = touches[0];
      const now = Date.now();
      const timeSinceLastTap = now - touchStateRef.current.lastTouchTime;
      const distanceFromLastTap = Math.sqrt(
        Math.pow(touch.clientX - touchStateRef.current.lastTapX, 2) +
        Math.pow(touch.clientY - touchStateRef.current.lastTapY, 2)
      );

      if (
        timeSinceLastTap < 300 &&
        timeSinceLastTap > 0 &&
        distanceFromLastTap < 50
      ) {
        e.preventDefault();
        if (touchStateRef.current.tapTimeout) {
          clearTimeout(touchStateRef.current.tapTimeout);
          touchStateRef.current.tapTimeout = null;
        }

        const container = containerRef.current;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const tapX = touch.clientX - containerRect.left;
          const tapY = touch.clientY - containerRect.top;

          if (zoomState.scale > 1) {
            setZoomState({ scale: 1, translateX: 0, translateY: 0 });
          } else {
            const newScale = 2;
            const newTranslateX =
              (-(tapX - containerRect.width / 2) * (newScale - 1)) / newScale;
            const newTranslateY =
              (-(tapY - containerRect.height / 2) * (newScale - 1)) / newScale;

            const constrained = constrainTranslate(
              newScale,
              newTranslateX,
              newTranslateY,
              containerRect.width,
              containerRect.height
            );

            setZoomState({
              scale: newScale,
              translateX: constrained.translateX,
              translateY: constrained.translateY,
            });
          }
        }
        touchStateRef.current.lastTouchTime = 0;
        return;
      }

      touchStateRef.current.lastPanX = touch.clientX;
      touchStateRef.current.lastPanY = touch.clientY;
      touchStateRef.current.startX = touch.clientX;
      touchStateRef.current.startY = touch.clientY;
      touchStateRef.current.hasMoved = false;
      setIsPanning(false);

      touchStateRef.current.lastTapX = touch.clientX;
      touchStateRef.current.lastTapY = touch.clientY;
      touchStateRef.current.lastTouchTime = now;
    } else if (touches.length === 2) {
      e.preventDefault();

      if (touchStateRef.current.tapTimeout) {
        clearTimeout(touchStateRef.current.tapTimeout);
        touchStateRef.current.tapTimeout = null;
      }

      const distance = getDistance(touches[0], touches[1]);
      const center = getCenter(touches[0], touches[1]);

      touchStateRef.current.initialDistance = distance;
      touchStateRef.current.initialScale = zoomState.scale;
      touchStateRef.current.initialTranslateX = zoomState.translateX;
      touchStateRef.current.initialTranslateY = zoomState.translateY;
      touchStateRef.current.centerX = center.x;
      touchStateRef.current.centerY = center.y;
      setIsZooming(true);
      setIsPanning(false);
    }
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    if (!isMobile || isLocal || !screenShareOn) return;

    const touches = e.touches;

    if (touches.length === 1 && zoomState.scale > 1) {
      const touch = touches[0];
      const deltaX = Math.abs(touch.clientX - touchStateRef.current.startX);
      const deltaY = Math.abs(touch.clientY - touchStateRef.current.startY);
      const movementThreshold = 10; // pixels

      if (
        !touchStateRef.current.hasMoved &&
        (deltaX > movementThreshold || deltaY > movementThreshold)
      ) {
        touchStateRef.current.hasMoved = true;
        setIsPanning(true);
      }

      if (touchStateRef.current.hasMoved) {
        e.preventDefault();
        const panDeltaX = touch.clientX - touchStateRef.current.lastPanX;
        const panDeltaY = touch.clientY - touchStateRef.current.lastPanY;

        const container = containerRef.current;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const constrained = constrainTranslate(
            zoomState.scale,
            zoomState.translateX + panDeltaX,
            zoomState.translateY + panDeltaY,
            containerRect.width,
            containerRect.height
          );

          setZoomState((prev) => ({
            ...prev,
            translateX: constrained.translateX,
            translateY: constrained.translateY,
          }));
        }

        touchStateRef.current.lastPanX = touch.clientX;
        touchStateRef.current.lastPanY = touch.clientY;
      }
    } else if (touches.length === 2 && isZooming) {
      // Two touches - pinch zoom
      e.preventDefault();
      const distance = getDistance(touches[0], touches[1]);
      const scaleChange = distance / touchStateRef.current.initialDistance;
      const newScale = Math.max(
        1,
        Math.min(5, touchStateRef.current.initialScale * scaleChange)
      );

      const container = containerRef.current;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const centerX = touchStateRef.current.centerX - containerRect.left;
        const centerY = touchStateRef.current.centerY - containerRect.top;

        const scaleDiff = newScale - touchStateRef.current.initialScale;
        const newTranslateX =
          touchStateRef.current.initialTranslateX -
          ((centerX - containerRect.width / 2) * scaleDiff) /
          touchStateRef.current.initialScale;
        const newTranslateY =
          touchStateRef.current.initialTranslateY -
          ((centerY - containerRect.height / 2) * scaleDiff) /
          touchStateRef.current.initialScale;

        const constrained = constrainTranslate(
          newScale,
          newTranslateX,
          newTranslateY,
          containerRect.width,
          containerRect.height
        );

        setZoomState({
          scale: newScale,
          translateX: constrained.translateX,
          translateY: constrained.translateY,
        });
      }
    }
  };

  // Handle touch end
  const handleTouchEnd = (e) => {
    if (!isMobile || isLocal || !screenShareOn) return;

    const touches = e.touches;

    if (touches.length === 0) {
      setIsZooming(false);
      setIsPanning(false);
      touchStateRef.current.hasMoved = false;
    } else if (touches.length === 1) {
      setIsZooming(false);
      const touch = touches[0];
      touchStateRef.current.lastPanX = touch.clientX;
      touchStateRef.current.lastPanY = touch.clientY;
      setIsPanning(touchStateRef.current.hasMoved && zoomState.scale > 1);
    }
  };

  const enterFullscreen = async (el) => {
    if (!el) return;
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen();
      } else if (el.msRequestFullscreen) {
        await el.msRequestFullscreen();
      }
    } catch (_) { }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
    } catch (_) { }
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement === containerRef.current) {
        await exitFullscreen();
      } else if (
        containerRef.current?.requestFullscreen ||
        containerRef.current?.webkitRequestFullscreen ||
        containerRef.current?.msRequestFullscreen
      ) {
        await enterFullscreen(containerRef.current);
      } else {
        throw new Error("Fullscreen API not available");
      }
    } catch (_) {
      try {
        document.body.classList.toggle("ios-pseudo-fullscreen");
        setIsFullscreen(
          document.body.classList.contains("ios-pseudo-fullscreen")
        );
      } catch (_) { }
    }
  };

  return (
    <div
      ref={containerRef}
      className={`bg-gray-750 rounded  relative overflow-hidden transition-all duration-300 ease-in-out ${sideBarMode ? "w-[98%]" : "w-full"
        }`}
      style={{ height: `${height}px` }}
    >
      <audio autoPlay playsInline controls={false} ref={audioPlayer} />
      <div
        ref={videoContainerRef}
        className={"video-contain absolute h-full w-full"}
        onDoubleClick={toggleFullscreen}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          touchAction: isMobile && !isLocal && screenShareOn ? "none" : "auto",
          overflow:
            isMobile && !isLocal && screenShareOn && zoomState.scale > 1
              ? "hidden"
              : "visible",
        }}
      >
        {/* Fullscreen toggle visible for viewers while screen share is shown */}
        {!isLocal && screenShareOn ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFullscreen();
            }}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            className="absolute z-30 top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded p-2 transition-colors"
          >
            <img
              src={isFullscreen ? "/icons/compact.svg" : "/icons/wide.svg"}
              alt={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              className="w-5 h-5"
            />
          </button>
        ) : null}
        <div
          style={{
            transform:
              isMobile && !isLocal && screenShareOn
                ? `translate(${zoomState.translateX}px, ${zoomState.translateY}px) scale(${zoomState.scale})`
                : "none",
            transformOrigin: "center center",
            transition:
              isZooming || isPanning ? "none" : "transform 0.1s ease-out",
            width: "100%",
            height: "100%",
          }}
        >
          <VideoPlayer
            participantId={presenterId} // Required
            type="share" // "video" or "share"
            containerStyle={{
              height: "100%",
              width: "100%",
            }}
            className="h-full"
            classNameVideo="h-full"
            videoStyle={{
              filter: isLocal ? "blur(1rem)" : undefined,
            }}
          />
        </div>

        {/* Raise Hand Overlay */}
        {hasRaisedHand && (
          <div className="absolute top-2 left-2 z-20">
            <div className="bg-orange-500 rounded-full p-2 shadow-lg">
              <img
                src="/icons/handIcon.png"
                alt="Raised Hand"
                className="w-6 h-6"
              />
            </div>
          </div>
        )}

        <div
          className="bottom-2 left-2 bg-gray-750 p-2 absolute rounded-md flex items-center justify-center"
          style={{
            transition: "all 200ms",
            transitionTimingFunction: "linear",
          }}
        >
          {!micOn ? (
            <MicOffSmallIcon fillcolor="white" />
          ) : micOn && isActiveSpeaker ? (
            <SpeakerIcon />
          ) : (
            <></>
          )}

          {/* <p className="text-sm text-white">
            {isLocal
              ? `You are presenting`
              : `${nameTructed(displayName, 15)} is presenting`}
          </p> */}
        </div>
        {isLocal ? (
          <>
            <div className="p-10 rounded-2xl flex flex-col items-center justify-center absolute top-1/2 left-1/2 bg-gray-750 transform -translate-x-1/2 -translate-y-1/2">
              <ScreenShareIcon
                style={{ height: 48, width: 48, color: "white" }}
              />
              <div className="mt-4">
                <p className="text-white text-xl font-semibold">
                  You are presenting to everyone
                </p>
              </div>
              <div className="mt-8 flex flex-row items-center gap-3 justify-center">
                <button
                  className="bg-[#7700ff] text-white px-4 py-2 rounded text-sm text-center font-medium"
                  onClick={(e) => {
                    e.stopPropagation();
                    mMeeting.toggleScreenShare();
                  }}
                >
                  STOP PRESENTING
                </button>
                <button
                  className={[
                    "bg-white text-black px-3 py-2 rounded text-sm text-center font-medium flex items-center justify-center",
                    !("pictureInPictureEnabled" in document) ||
                      !document.pictureInPictureEnabled
                      ? "opacity-60 cursor-not-allowed"
                      : "",
                  ].join(" ")}
                  onClick={async (e) => {
                    e.stopPropagation();

                    const pipSupported =
                      "pictureInPictureEnabled" in document &&
                      document.pictureInPictureEnabled;
                    if (!pipSupported) return;


                    if (pipMode) {
                      try {

                        await document.exitPictureInPicture();
                      } catch (err) {
                        logger("error", "Error exiting PiP", err);
                      }
                      return;
                    }


                    if (restoreScreenSharePipRef?.current) {
                      restoreScreenSharePipRef.current();
                      return;
                    }


                    if (!pipClosedByUser) {
                      setPipClosedByUser(true);
                    }
                  }}
                  title={
                    !("pictureInPictureEnabled" in document) ||
                      !document.pictureInPictureEnabled
                      ? "PiP not supported"
                      : pipMode
                        ? "Hide PiP Window"
                        : "Show PiP Window"
                  }
                  aria-label={pipMode ? "Hide PiP Window" : "Show PiP Window"}
                >
                  {pipMode ? (
                    <img src="/icons/closePip.svg" alt="Close PiP Window" className="w-5 h-5" />
                  ) : (
                    <img src="/icons/openPip.svg" alt="Open PiP Window" className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            <CornerDisplayName
              {...{
                isLocal,
                displayName,
                micOn,
                webcamOn,
                isPresenting: true,
                participantId: presenterId,
                isActiveSpeaker,
              }}
            />
          </>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}
